/* ==========================================================
   LIME LABS COMMERCE LAYER
   C-06B MEGA-MENU RECOVERY — COMPATIBILITY OVERLAY
   ----------------------------------------------------------
   Preserves V6.4 ownership. No second menu controller.
   - Keeps real parent anchors natively navigable on click.
   - Placeholder parents (#) retain V6.4 click-to-open behavior.
   - Reinforces trigger -> panel pointer/focus continuity.
   - Desktop only; mobile drawer is untouched.
   ========================================================== */

(() => {
  'use strict';

  const desktop = window.matchMedia('(min-width: 700px)');
  const HEADER = '.ll-header--llcl';
  const TRIGGER = '[data-ll-menu-trigger]';
  const BLOCK = '[data-ll-mega-block]';

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const blockForTrigger = (header, trigger) => {
    const key = normalize(trigger?.dataset?.llMenuTrigger);
    if (!key) return null;

    return Array.from(header.querySelectorAll(BLOCK)).find((block) => {
      const blockKey = normalize(
        block.dataset.llMenuKey ||
        block.querySelector('[data-ll-menu-key]')?.dataset?.llMenuKey
      );
      return blockKey === key;
    }) || null;
  };

  const isPlaceholderHref = (trigger) => {
    const raw = String(trigger?.getAttribute('href') || '').trim();
    return raw === '' || raw === '#' || raw.startsWith('javascript:');
  };

  const clearControllerCloseTimer = (header) => {
    if (!header) return;
    header.dispatchEvent(new Event('pointerenter'));
  };

  const bind = (header) => {
    if (!header || header.dataset.llC06bBound === 'true') return;
    header.dataset.llC06bBound = 'true';

    header.querySelectorAll(TRIGGER).forEach((trigger) => {
      const block = blockForTrigger(header, trigger);
      if (!block) return;

      trigger.addEventListener('click', (event) => {
        if (!desktop.matches || isPlaceholderHref(trigger)) return;
        event.stopImmediatePropagation();
      }, { capture: true });

      block.addEventListener('pointerenter', (event) => {
        if (!desktop.matches || event.pointerType === 'touch') return;
        clearControllerCloseTimer(header);
      });

      block.addEventListener('focusin', () => {
        if (!desktop.matches) return;
        clearControllerCloseTimer(header);
      });
    });
  };

  const boot = () => {
    document.querySelectorAll(HEADER).forEach(bind);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll(HEADER).forEach((header) => {
      delete header.dataset.llC06bBound;
    });
    boot();
  });
})();

/* ==========================================================
   C-06C — HUMAN QA RECOVERY
   ----------------------------------------------------------
   Stage 1 only.

   DESIGN RULE:
   Existing LLCL visual grammar is the source of truth.
   - Home Decor: native block restored; this script does not touch it.
   - Unisex: native collections block; this script does not touch it.
   - Editions: native Mujer/Descubrir collections grammar.
   - The Icons: native collections grammar + top-sellers source.
   - Nuevo: Journal-inspired masthead + restrained horizontal rail.
   - Descubrir: native collections block, augmented only with missing destinations.
   - Journal / Mujer remain untouched.

   V6.4 remains the owner of top-level open/close, focus,
   ESC, inert states, outside click, and panel state.
   ========================================================== */

(() => {
  'use strict';

  const desktop = window.matchMedia('(min-width: 700px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HEADER = '.ll-header--llcl';
  const LAYER = `${HEADER} [data-ll-mega-layer]`;
  const BLOCK = '[data-ll-mega-block]';
  const V64 = '__LLCL_MEGA_MENU_V64__';
  const routeRoot = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const locale = String(document.documentElement.lang || 'es').trim().toLowerCase().split('-')[0];
  const language = locale === 'en' ? 'en' : 'es';
  const COPY = {
    es: {
      product: 'Producto',
      soldOut: 'Agotado',
      collection: 'COLECCIÓN',
      viewCollection: 'Ver colección',
      collectionMediaUnavailable: 'MEDIOS DE COLECCIÓN NO DISPONIBLES',
      collectionIndex: 'ÍNDICE DE COLECCIONES',
      edition: 'EDICIÓN',
      viewLatidos: 'Ver Latidos',
      viewHuichol: 'Ver Huichol',
      viewCrisol: 'Ver Crisol',
      viewNativo: 'Ver Nativo',
      selectionEyebrow: 'LA TROUPE / SELECCIÓN',
      viewTheIcons: 'Ver The Icons',
      iconsChrome: 'THE ICONS / ARCHIVO',
      newLabel: 'Nuevo',
      newContext: 'Nuevas llegadas · La Troupe',
      newAria: 'Nuevas llegadas',
      newFallback: 'Ver Nuevas Llegadas',
      newChrome: 'NOVEDADES / EDICIÓN ACTUAL',
      journey: 'El Viaje',
      city: 'La Ciudad',
      beach: 'La Playa',
      discoverJourneyChrome: 'DESCUBRIR / EL VIAJE',
      discoverCityChrome: 'DESCUBRIR / LA CIUDAD',
      discoverBeachChrome: 'DESCUBRIR / LA PLAYA'
    },
    en: {
      product: 'Product',
      soldOut: 'Sold out',
      collection: 'COLLECTION',
      viewCollection: 'View collection',
      collectionMediaUnavailable: 'COLLECTION MEDIA UNAVAILABLE',
      collectionIndex: 'COLLECTION INDEX',
      edition: 'EDITION',
      viewLatidos: 'View Latidos',
      viewHuichol: 'View Huichol',
      viewCrisol: 'View Crisol',
      viewNativo: 'View Nativo',
      selectionEyebrow: 'LA TROUPE / SELECTION',
      viewTheIcons: 'View The Icons',
      iconsChrome: 'THE ICONS / ARCHIVE',
      newLabel: 'New',
      newContext: 'New arrivals · La Troupe',
      newAria: 'New arrivals',
      newFallback: 'View New Arrivals',
      newChrome: 'NEWNESS / CURRENT EDIT',
      journey: 'The Journey',
      city: 'The City',
      beach: 'The Beach',
      discoverJourneyChrome: 'DISCOVER / THE JOURNEY',
      discoverCityChrome: 'DISCOVER / THE CITY',
      discoverBeachChrome: 'DISCOVER / THE BEACH'
    }
  };
  const t = (key) => COPY[language]?.[key] ?? COPY.es[key] ?? key;

  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  const storePath = (path) => {
    const clean = String(path || '').replace(/^\//, '');
    return `${routeRoot}${clean}`;
  };

  const findTopTrigger = (key) =>
    document.querySelector(`${HEADER} [data-ll-menu-trigger="${key}"]`);

  const ensureShell = (key, extraClass) => {
    const layer = document.querySelector(LAYER);
    if (!layer) return null;

    let shell = layer.querySelector(`${BLOCK}[data-ll-menu-key="${key}"]`);
    if (shell) return shell;

    shell = make(
      'div',
      `ll-mega-menu-block ll-mega-menu ll-mega-menu--dynamic ${extraClass || ''}`.trim()
    );
    shell.dataset.llMegaMenu = '';
    shell.dataset.llMegaBlock = '';
    shell.dataset.llMenuKey = key;
    shell.setAttribute('aria-busy', 'true');
    layer.appendChild(shell);
    return shell;
  };

  const ensureRequiredShells = () => {
    ensureShell('nuevo', 'll-c06c-newness-shell');
    ensureShell('the-icons', 'll-mega-menu--collections ll-mega-menu--media-right ll-c06c-icons-native');
  };

  ensureRequiredShells();

  const imageUrl = (product) => {
    const featured = product?.featured_image;
    if (typeof featured === 'string') return featured;
    if (featured?.src) return featured.src;
    const first = product?.images?.[0];
    if (typeof first === 'string') return first;
    return first?.src || '';
  };

  const imageAlt = (product) => {
    const first = product?.images?.[0];
    if (typeof first === 'object' && first?.alt) return first.alt;
    return product?.title || '';
  };

  const fetchProducts = async (handle, limit = 12) => {
    const url = `${storePath(`collections/${encodeURIComponent(handle)}/products.json`)}?limit=${limit}`;
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Collection ${handle} returned ${response.status}`);
    const payload = await response.json();
    return (payload?.products || []).filter((product) => product?.handle && imageUrl(product));
  };

  const arrowIcon = (className = '') => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    if (className) svg.setAttribute('class', className);
    svg.setAttribute('viewBox', '0 0 18 10');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M1 5H16M12 1L16 5L12 9');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.appendChild(path);
    return svg;
  };

  const productHref = (product) => storePath(`products/${encodeURIComponent(product.handle)}`);

  const buildEditorialProduct = (product, index, extraClass = '') => {
    const article = make('article', `ll-mega-editorial-product ll-mega-editorial-product--${index} ${index === 1 ? 'll-mega-editorial-product--hero' : 'll-mega-editorial-product--supporting'} ${extraClass}`.trim());
    article.dataset.llEditorialProduct = '';
    article.dataset.productIndex = String(index);
    if (product?.available === false) article.classList.add('is-unavailable');
    const link = make('a', 'll-mega-editorial-product__link');
    link.href = productHref(product);
    link.setAttribute('aria-label', product.title || t('product'));
    const media = make('div', 'll-mega-editorial-product__media');
    const image = make('img', 'll-mega-editorial-product__image');
    image.src = imageUrl(product);
    image.alt = imageAlt(product);
    image.loading = 'lazy';
    image.decoding = 'async';
    media.appendChild(image);
    media.appendChild(make('span', 'll-mega-editorial-product__edge'));
    const mediaIndex = make('span', 'll-mega-editorial-product__media-index', String(index).padStart(2, '0'));
    mediaIndex.setAttribute('aria-hidden', 'true');
    media.appendChild(mediaIndex);
    if (product?.available === false) media.appendChild(make('span', 'll-mega-editorial-product__availability', t('soldOut')));
    const caption = make('div', 'll-mega-editorial-product__caption');
    const captionIndex = make('span', 'll-mega-editorial-product__index', String(index).padStart(2, '0'));
    captionIndex.setAttribute('aria-hidden', 'true');
    caption.appendChild(captionIndex);
    caption.appendChild(make('span', 'll-mega-editorial-product__title', product.title || ''));
    link.append(media, caption);
    article.appendChild(link);
    return article;
  };

  const buildCollectionPanel = async (entry, panel) => {
    const wrapper = make('div', 'll-mega-editorial-collection ll-mega-editorial-collection--collections');
    wrapper.dataset.llEditorialCollection = '';
    wrapper.dataset.collectionHandle = entry.sourceHandle;
    const header = make('header', 'll-mega-editorial-collection__header');
    const identity = make('div', 'll-mega-editorial-collection__identity');
    identity.appendChild(make('span', 'll-mega-editorial-collection__eyebrow', entry.eyebrow || t('collection')));
    const rule = make('span', 'll-mega-editorial-collection__identity-rule');
    rule.setAttribute('aria-hidden', 'true');
    identity.appendChild(rule);
    identity.appendChild(make('span', 'll-mega-editorial-collection__name', entry.title));
    const view = make('a', 'll-mega-editorial-collection__view');
    view.href = entry.href;
    view.appendChild(make('span', '', entry.cta || t('viewCollection')));
    view.appendChild(arrowIcon('ll-mega-editorial-collection__view-icon'));
    header.append(identity, view);
    wrapper.appendChild(header);
    try {
      const products = await fetchProducts(entry.sourceHandle, 8);
      const selected = products.slice(0, 3);
      if (selected.length) {
        const productsNode = make('div', 'll-mega-editorial-collection__products');
        productsNode.dataset.llEditorialProducts = '';
        selected.forEach((product, index) => productsNode.appendChild(buildEditorialProduct(product, index + 1)));
        wrapper.appendChild(productsNode);
      } else {
        const empty = make('div', 'll-mega-editorial-collection__empty', t('collectionMediaUnavailable'));
        empty.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(empty);
      }
    } catch (_error) {
      const empty = make('div', 'll-mega-editorial-collection__empty', t('collectionMediaUnavailable'));
      empty.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(empty);
    }
    const chrome = make('div', 'll-mega-editorial-collection__chrome');
    chrome.setAttribute('aria-hidden', 'true');
    chrome.appendChild(make('span', '', 'LA TROUPE'));
    chrome.appendChild(make('span', 'll-mega-editorial-collection__chrome-rule'));
    chrome.appendChild(make('span', '', entry.chrome || t('collectionIndex')));
    chrome.appendChild(make('span', '', '01 / 03'));
    wrapper.appendChild(chrome);
    panel.replaceChildren(wrapper);
  };

  const normalizeWorldShell = (shell, worldClass) => {
    if (!shell) return;
    ['ll-mega-menu--commerce','ll-mega-menu--editorial','ll-mega-menu--journal','ll-mega-menu--boutiques','ll-mega-menu--media-left','ll-mega-menu--c06c','ll-c06c-home','ll-c06c-unisex','ll-c06c-editions','ll-c06c-icons'].forEach((className) => shell.classList.remove(className));
    shell.classList.add('ll-mega-menu','ll-mega-menu--dynamic','ll-mega-menu--collections','ll-mega-menu--media-right','ll-c06c-native-world',worldClass);
    shell.dataset.llMegaMode = 'collections';
  };

  const buildNativeCollectionsWorld = (key, entries, worldClass) => {
    const shell = ensureShell(key, '');
    if (!shell) return Promise.resolve();
    normalizeWorldShell(shell, worldClass);
    shell.dataset.llC06cHydrated = 'true';
    shell.removeAttribute('aria-busy');
    shell.replaceChildren();
    const nav = make('nav', 'll-mega-menu__nav ll-mega-menu__nav--editorial ll-mega-menu__nav--collections');
    nav.setAttribute('aria-label', key === 'the-icons' ? 'The Icons' : 'Editions');
    const list = make('ul', 'll-mega-menu__editorial-list unstyled-list');
    list.setAttribute('role', 'list');
    nav.appendChild(list);
    const aside = make('aside', 'll-mega-menu__editorial ll-mega-menu__editorial--universal');
    aside.dataset.llMediaStage = '';
    aside.setAttribute('aria-label', key === 'the-icons' ? 'The Icons' : 'Editions');
    const stage = make('div', 'll-mega-menu__collection-stage');
    aside.appendChild(stage);
    const jobs = [];
    entries.forEach((entry, index) => {
      const panelId = `ll-c06c-${key}-${entry.key}`;
      const item = make('li', 'll-mega-menu__editorial-item');
      const link = make('a', 'll-mega-menu__heading ll-mega-menu__heading--editorial', entry.title);
      link.href = entry.href;
      link.dataset.llItemKey = entry.key;
      link.dataset.llParentKey = '';
      link.dataset.llMegaTrigger = panelId;
      item.appendChild(link);
      list.appendChild(item);
      const panel = make('section', `ll-mega-hover-panel ll-mega-hover-panel--editorial-collection${index === 0 ? ' is-active' : ''}`);
      panel.dataset.llMegaPanel = panelId;
      panel.dataset.llAutoMedia = '';
      panel.dataset.llItemKey = entry.key;
      panel.dataset.llParentKey = '';
      panel.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      stage.appendChild(panel);
      jobs.push(buildCollectionPanel(entry, panel));
      if (index === 0) shell.dataset.llMegaDefaultPanel = panelId;
    });
    shell.append(nav, aside);
    return Promise.allSettled(jobs);
  };

  const augmentNativeCollectionsWorld = (key, entries) => {
    const shell = document.querySelector(`${BLOCK}[data-ll-menu-key="${key}"]`);
    if (!shell) return Promise.resolve();
    const list = shell.querySelector('.ll-mega-menu__editorial-list');
    const stage = shell.querySelector('.ll-mega-menu__collection-stage');
    if (!list || !stage) return Promise.resolve();
    const jobs = [];
    entries.forEach((entry) => {
      const alreadyExists = Array.from(list.querySelectorAll('a[href]')).some((link) => {
        const existingKey = String(link.dataset.llItemKey || '').trim().toLowerCase();
        if (existingKey === entry.key) return true;
        try {
          const existingPath = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
          const entryPath = new URL(entry.href, window.location.origin).pathname.replace(/\/$/, '');
          return existingPath === entryPath;
        } catch (_error) { return false; }
      });
      if (alreadyExists) return;
      const panelId = `ll-c06c-${key}-${entry.key}`;
      const item = make('li', 'll-mega-menu__editorial-item');
      const link = make('a', 'll-mega-menu__heading ll-mega-menu__heading--editorial', entry.title);
      link.href = entry.href;
      link.dataset.llItemKey = entry.key;
      link.dataset.llParentKey = '';
      link.dataset.llMegaTrigger = panelId;
      item.appendChild(link);
      list.appendChild(item);
      const panel = make('section', 'll-mega-hover-panel ll-mega-hover-panel--editorial-collection');
      panel.dataset.llMegaPanel = panelId;
      panel.dataset.llAutoMedia = '';
      panel.dataset.llItemKey = entry.key;
      panel.dataset.llParentKey = '';
      panel.setAttribute('aria-hidden', 'true');
      stage.appendChild(panel);
      jobs.push(buildCollectionPanel(entry, panel));
    });
    if (jobs.length) shell.dataset.llC06cAugmented = 'true';
    return Promise.allSettled(jobs);
  };

  const topTriggerDestination = (key, fallback) => {
    const trigger = findTopTrigger(key);
    const href = String(trigger?.getAttribute('href') || '').trim();
    return href && href !== '#' ? href : fallback;
  };

  const buildNewnessCard = (product, index) => buildEditorialProduct(product, index, 'll-c06c-newness__card');

  const setupNewnessAutoplay = (track) => {
    if (!track || reducedMotion.matches) return;
    let timer = null;
    let paused = false;
    const clear = () => { if (timer) window.clearInterval(timer); timer = null; };
    const stop = () => { paused = true; clear(); };
    const start = () => {
      if (paused || timer || reducedMotion.matches) return;
      timer = window.setInterval(() => {
        if (!desktop.matches || paused || document.hidden) return;
        const firstCard = track.querySelector('.ll-c06c-newness__card');
        const style = window.getComputedStyle(track);
        const gap = Number.parseFloat(style.columnGap || style.gap) || 0;
        const step = firstCard ? firstCard.getBoundingClientRect().width + gap : 250;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - step * 0.55;
        track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + step, behavior: 'smooth' });
      }, 3600);
    };
    track.addEventListener('pointerenter', stop);
    track.addEventListener('pointerleave', () => { paused = false; start(); });
    track.addEventListener('focusin', stop);
    track.addEventListener('focusout', () => window.setTimeout(() => { if (!track.contains(document.activeElement)) { paused = false; start(); } }, 0));
    document.addEventListener('visibilitychange', () => { if (document.hidden) clear(); else if (!paused) start(); });
    start();
  };

  const renderNuevo = async () => {
    const shell = ensureShell('nuevo', 'll-c06c-newness-shell');
    if (!shell) return;
    shell.classList.remove('ll-mega-menu--commerce','ll-mega-menu--collections','ll-mega-menu--editorial','ll-mega-menu--journal','ll-mega-menu--boutiques','ll-mega-menu--media-left','ll-mega-menu--media-right','ll-mega-menu--c06c');
    shell.classList.add('ll-c06c-newness-shell');
    shell.dataset.llMegaMode = 'newness';
    shell.dataset.llC06cHydrated = 'true';
    shell.replaceChildren();
    const root = make('section', 'll-c06c-newness');
    const masthead = make('header', 'll-c06c-newness__masthead');
    const mastheadLeft = make('div', 'll-c06c-newness__masthead-left');
    mastheadLeft.appendChild(make('span', 'll-c06c-newness__section-label', t('newLabel')));
    const rule = make('span', 'll-c06c-newness__rule');
    rule.setAttribute('aria-hidden', 'true');
    mastheadLeft.appendChild(rule);
    mastheadLeft.appendChild(make('span', 'll-c06c-newness__context', t('newContext')));
    const destination = topTriggerDestination('nuevo', storePath('collections/nuevo'));
    const cta = make('a', 'll-c06c-newness__cta');
    cta.href = destination;
    cta.appendChild(make('span', '', t('viewCollection')));
    cta.appendChild(arrowIcon('ll-c06c-newness__cta-icon'));
    masthead.append(mastheadLeft, cta);
    const viewport = make('div', 'll-c06c-newness__viewport');
    const track = make('div', 'll-c06c-newness__track');
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', t('newAria'));
    viewport.appendChild(track);
    const footer = make('footer', 'll-c06c-newness__footer');
    footer.appendChild(make('span', '', 'LA TROUPE'));
    footer.appendChild(make('span', 'll-c06c-newness__footer-rule'));
    footer.appendChild(make('span', '', t('newChrome')));
    root.append(masthead, viewport, footer);
    shell.appendChild(root);
    try {
      const products = await fetchProducts('nuevo', 16);
      products.slice(0, 12).forEach((product, index) => track.appendChild(buildNewnessCard(product, index + 1)));
      setupNewnessAutoplay(track);
    } catch (_error) {
      const fallback = make('a', 'll-c06c-newness__fallback', t('newFallback'));
      fallback.href = destination;
      track.appendChild(fallback);
    }
    shell.removeAttribute('aria-busy');
  };

  const renderEditions = () => buildNativeCollectionsWorld('editions', [
    { key: 'latidos', title: 'Latidos', href: storePath('collections/latidos'), sourceHandle: 'latidos', eyebrow: t('edition'), cta: t('viewLatidos'), chrome: 'EDITIONS / LATIDOS' },
    { key: 'huichol', title: 'Huichol', href: storePath('collections/huichol'), sourceHandle: 'huichol', eyebrow: t('edition'), cta: t('viewHuichol'), chrome: 'EDITIONS / HUICHOL' },
    { key: 'crisol', title: 'Crisol', href: storePath('collections/crisol'), sourceHandle: 'crisol', eyebrow: t('edition'), cta: t('viewCrisol'), chrome: 'EDITIONS / CRISOL' },
    { key: 'nativo', title: 'Nativo', href: storePath('collections/nativo-home'), sourceHandle: 'nativo-home', eyebrow: t('edition'), cta: t('viewNativo'), chrome: 'EDITIONS / NATIVO' }
  ], 'll-c06c-editions-native');

  const renderIcons = () => buildNativeCollectionsWorld('the-icons', [
    { key: 'the-icons', title: 'The Icons', href: topTriggerDestination('the-icons', storePath('collections/top-sellers')), sourceHandle: 'top-sellers', eyebrow: t('selectionEyebrow'), cta: t('viewTheIcons'), chrome: t('iconsChrome') }
  ], 'll-c06c-icons-native');

  const renderDescubrir = () => augmentNativeCollectionsWorld('descubrir', [
    { key: 'le-voyage', title: t('journey'), href: storePath('collections/le-voyage'), sourceHandle: 'le-voyage', eyebrow: t('collection'), cta: t('viewCollection'), chrome: t('discoverJourneyChrome') },
    { key: 'la-ville', title: t('city'), href: storePath('collections/la-ville'), sourceHandle: 'la-ville', eyebrow: t('collection'), cta: t('viewCollection'), chrome: t('discoverCityChrome') },
    { key: 'la-plage', title: t('beach'), href: storePath('collections/la-plage'), sourceHandle: 'la-plage', eyebrow: t('collection'), cta: t('viewCollection'), chrome: t('discoverBeachChrome') }
  ]);

  const refreshV64 = () => {
    const api = window[V64];
    if (api && typeof api.refresh === 'function') api.refresh();
  };

  const hydrate = async () => {
    if (!desktop.matches) return;
    const jobs = [renderNuevo(), renderEditions(), renderIcons(), renderDescubrir()];
    refreshV64();
    await Promise.allSettled(jobs);
    refreshV64();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hydrate, { once: true });
  else hydrate();

  if (typeof desktop.addEventListener === 'function') desktop.addEventListener('change', (event) => { if (event.matches) hydrate(); });

  document.addEventListener('shopify:section:load', () => {
    ensureRequiredShells();
    window.setTimeout(hydrate, 0);
  }, true);
})();

/* ==========================================================
   C-06C — DESCUBRIR MEDIA RECOVERY
   ----------------------------------------------------------
   Real collection media fallback + explicit hover/focus binding
   for the three added discovery destinations.
   ========================================================== */
(() => {
  'use strict';
  const HEADER = '.ll-header--llcl';
  const BLOCK = '[data-ll-mega-block]';
  const KEY = 'descubrir';
  const FALLBACKS = {
    'le-voyage': [
      { title: 'Kimono Trenza', handle: 'kimono-trenza-crema', image: 'https://cdn.shopify.com/s/files/1/1981/5461/files/la-troupe-kimono-trenza-01.jpg?v=1789173022' },
      { title: 'Pantalon San Cristobal', handle: 'pantalon-san-cristobal', image: 'https://cdn.shopify.com/s/files/1/1981/5461/files/FinalEdit-9.jpg?v=1729865324' },
      { title: 'Blusa Magnolia', handle: 'blusa-magnolia', image: 'https://cdn.shopify.com/s/files/1/1981/5461/files/blusa-magnolia-ivory-frontal-la-troupe.png?v=1789148904' }
    ],
    'la-ville': [
      { title: 'Vestido Nudo Elau', handle: 'vestido-nudo-negro', image: 'https://cdn.shopify.com/s/files/1/1981/5461/products/la-troupe-vestido-nudo-elau-01.jpg?v=1789220561' },
      { title: 'Pantalon San Cristobal', handle: 'pantalon-san-cristobal', image: 'https://cdn.shopify.com/s/files/1/1981/5461/files/FinalEdit-9.jpg?v=1729865324' },
      { title: 'Kimono Trenza', handle: 'kimono-trenza-crema', image: 'https://cdn.shopify.com/s/files/1/1981/5461/files/la-troupe-kimono-trenza-01.jpg?v=1789173022' }
    ],
    'la-plage': [
      { title: 'Kimono Trenza', handle: 'kimono-trenza-crema', image: 'https://cdn.shopify.com/s/files/1/1981/5461/files/la-troupe-kimono-trenza-01.jpg?v=1789173022' },
      { title: 'Vestido Nudo Elau', handle: 'vestido-nudo-negro', image: 'https://cdn.shopify.com/s/files/1/1981/5461/products/la-troupe-vestido-nudo-elau-01.jpg?v=1789220561' },
      { title: 'Tunica Joaquina', handle: 'tunica-joaquina', image: 'https://cdn.shopify.com/s/files/1/1981/5461/files/AV6A1811copy.jpg?v=1772765063' }
    ]
  };
  const storeRoot = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const getShell = () => document.querySelector(`${HEADER} ${BLOCK}[data-ll-menu-key="${KEY}"]`);
  const productUrl = (handle) => `${storeRoot}products/${encodeURIComponent(handle)}`;

  const makeProduct = (product, index) => {
    const article = document.createElement('article');
    article.className = `ll-mega-editorial-product ll-mega-editorial-product--${index} ${index === 1 ? 'll-mega-editorial-product--hero' : 'll-mega-editorial-product--supporting'}`;
    article.dataset.llEditorialProduct = '';
    article.dataset.productIndex = String(index);
    const link = document.createElement('a');
    link.className = 'll-mega-editorial-product__link';
    link.href = productUrl(product.handle);
    link.setAttribute('aria-label', product.title);
    const media = document.createElement('div');
    media.className = 'll-mega-editorial-product__media';
    const image = document.createElement('img');
    image.className = 'll-mega-editorial-product__image';
    image.src = product.image;
    image.alt = product.title;
    image.loading = 'lazy';
    image.decoding = 'async';
    media.appendChild(image);
    const edge = document.createElement('span');
    edge.className = 'll-mega-editorial-product__edge';
    edge.setAttribute('aria-hidden', 'true');
    media.appendChild(edge);
    const mediaIndex = document.createElement('span');
    mediaIndex.className = 'll-mega-editorial-product__media-index';
    mediaIndex.textContent = String(index).padStart(2, '0');
    mediaIndex.setAttribute('aria-hidden', 'true');
    media.appendChild(mediaIndex);
    const caption = document.createElement('div');
    caption.className = 'll-mega-editorial-product__caption';
    const captionIndex = document.createElement('span');
    captionIndex.className = 'll-mega-editorial-product__index';
    captionIndex.textContent = String(index).padStart(2, '0');
    captionIndex.setAttribute('aria-hidden', 'true');
    const title = document.createElement('span');
    title.className = 'll-mega-editorial-product__title';
    title.textContent = product.title;
    caption.append(captionIndex, title);
    link.append(media, caption);
    article.appendChild(link);
    return article;
  };

  const injectFallbackProducts = (panel, key) => {
    if (!panel || panel.querySelector('.ll-mega-editorial-product__image')) return true;
    const wrapper = panel.querySelector('.ll-mega-editorial-collection');
    if (!wrapper) return false;
    wrapper.querySelector('.ll-mega-editorial-collection__empty')?.remove();
    let productsNode = wrapper.querySelector('[data-ll-editorial-products]');
    if (!productsNode) {
      productsNode = document.createElement('div');
      productsNode.className = 'll-mega-editorial-collection__products';
      productsNode.dataset.llEditorialProducts = '';
      const chrome = wrapper.querySelector('.ll-mega-editorial-collection__chrome');
      if (chrome) wrapper.insertBefore(productsNode, chrome);
      else wrapper.appendChild(productsNode);
    }
    productsNode.replaceChildren();
    (FALLBACKS[key] || []).forEach((product, index) => productsNode.appendChild(makeProduct(product, index + 1)));
    return Boolean(productsNode.querySelector('.ll-mega-editorial-product__image'));
  };

  const activate = (shell, link, panel) => {
    if (!shell || !link || !panel) return;
    shell.querySelectorAll('[data-ll-mega-panel]').forEach((candidate) => {
      const active = candidate === panel;
      candidate.classList.toggle('is-active', active);
      candidate.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    shell.querySelectorAll('.ll-mega-menu__nav [data-ll-item-key]').forEach((candidate) => {
      const active = candidate === link;
      candidate.classList.toggle('is-media-active', active);
      if (active) candidate.setAttribute('data-ll-media-active', 'true');
      else candidate.removeAttribute('data-ll-media-active');
    });
    shell.dataset.llActivePanel = panel.dataset.llMegaPanel || '';
  };

  const bindEntry = (shell, key) => {
    const link = shell.querySelector(`.ll-mega-menu__nav [data-ll-item-key="${key}"]`);
    const panel = shell.querySelector(`[data-ll-mega-panel="ll-c06c-${KEY}-${key}"]`);
    if (!link || !panel) return;
    if (link.dataset.llC06cDiscoverMediaBound !== 'true') {
      const show = () => activate(shell, link, panel);
      link.addEventListener('pointerenter', show);
      link.addEventListener('focusin', show);
      link.dataset.llC06cDiscoverMediaBound = 'true';
    }
    let attempt = 0;
    const ensureMedia = () => {
      if (panel.querySelector('.ll-mega-editorial-product__image')) return;
      if (injectFallbackProducts(panel, key)) return;
      attempt += 1;
      if (attempt < 6) window.setTimeout(ensureMedia, 250);
    };
    ensureMedia();
  };

  const boot = () => {
    const shell = getShell();
    if (!shell) return;
    Object.keys(FALLBACKS).forEach((key) => bindEntry(shell, key));
  };
  const scheduleBoot = () => {
    boot();
    window.setTimeout(boot, 150);
    window.setTimeout(boot, 600);
    window.setTimeout(boot, 1400);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleBoot, { once: true });
  else scheduleBoot();
  document.addEventListener('shopify:section:load', () => window.setTimeout(scheduleBoot, 0), true);
})();
